import type { DeviceStatusColor } from '@/types/deviceStatus'
import type { IconName } from 'lucide-react/dynamic'

export const DEFAULT_DEVICE_STATUS_ICON: IconName = 'activity'

/** Matches the dashboard panels' default marker color. */
export const DEFAULT_DEVICE_STATUS_COLOR = '#3b82f6'

/**
 * One-click hues for the status picker, spanning the wheel while every entry still clears
 * `MIN_STATUS_CONTRAST` in both themes — a preset that warns the moment it's picked would be a
 * built-in trap. That rules out the bright yellows and ambers, hence the darker olive below.
 * `deviceStatus.test.ts` holds the line.
 */
export const DEVICE_STATUS_COLOR_PRESETS = [
	'#64748b',
	DEFAULT_DEVICE_STATUS_COLOR,
	'#8b5cf6',
	'#0891b2',
	'#059669',
	'#9a7b0a',
	'#ea580c',
	'#ec4899',
	'#ef4444',
] as const

/**
 * Accepts `#rgb`, `#rrggbb`, and either without the leading `#`, returning the canonical `#rrggbb`
 * or null. Shorthand is expanded so every stored value is a single, alpha-appendable form.
 */
export const normalizeHexColor = (value: string): string | null => {
	const raw = value.trim().replace(/^#/, '')
	if (/^[0-9a-f]{3}$/i.test(raw)) {
		return `#${raw
			.split('')
			.map(char => char + char)
			.join('')}`.toLowerCase()
	}
	return /^[0-9a-f]{6}$/i.test(raw) ? `#${raw}`.toLowerCase() : null
}

export const isValidHexColor = (value: string): boolean => normalizeHexColor(value) !== null

/** Full-strength text over the same hue at low alpha, since the color is author-chosen not tokenized. */
export const deviceStatusBadgeStyle = (color: DeviceStatusColor) => ({
	color,
	backgroundColor: `${color}1a`,
	borderColor: `${color}59`,
})

/**
 * Relative luminance of the `--background` token per theme, derived from `lch(97% 0 0)` and
 * `lch(7.75% 0 0)` in index.css. A status color is inline rather than tokenized, so the same value
 * has to stay legible in both themes — there is no dark-mode variant to fall back on.
 */
const LIGHT_BACKGROUND_LUMINANCE = 0.9244
const DARK_BACKGROUND_LUMINANCE = 0.0086

const channelLuminance = (channel: number): number => {
	const c = channel / 255
	return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export const relativeLuminance = (color: string): number | null => {
	const hex = normalizeHexColor(color)
	if (!hex) {
		return null
	}
	const [r, g, b] = [1, 3, 5].map(index => Number.parseInt(hex.slice(index, index + 2), 16))
	return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)
}

const contrastRatio = (a: number, b: number): number => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)

/**
 * WCAG 1.4.11 (non-text / UI component) threshold rather than the 4.5:1 for body text: the badge is a
 * short label on a tinted chip, and 4.5 would reject most mid-tone hues including the default blue.
 */
export const MIN_STATUS_CONTRAST = 3

export interface StatusContrast {
	light: number
	dark: number
	/**
	 * Null when the color is legible in both themes. Only ever one theme: at a 3:1 threshold the two
	 * failure bands are disjoint (light fails above ~0.275 luminance, dark below ~0.126), so raising
	 * `MIN_STATUS_CONTRAST` would make failing both possible and this type would need a third case.
	 */
	failsOn: 'light' | 'dark' | null
}

export const checkStatusContrast = (color: string): StatusContrast | null => {
	const luminance = relativeLuminance(color)
	if (luminance === null) {
		return null
	}
	const light = contrastRatio(luminance, LIGHT_BACKGROUND_LUMINANCE)
	const dark = contrastRatio(luminance, DARK_BACKGROUND_LUMINANCE)
	return {
		light,
		dark,
		failsOn: light < MIN_STATUS_CONTRAST ? 'light' : dark < MIN_STATUS_CONTRAST ? 'dark' : null,
	}
}

/** Each status costs one /search per page render, so the count is capped rather than unbounded. */
export const MAX_DEVICE_STATUSES = 8

/**
 * Reserved derived-column name: a project that defines `status` drives the device list's leading icon
 * from it instead of the heartbeat. Renaming that column reverts the icon to the heartbeat.
 */
export const DEVICE_STATUS_COLUMN_NAME = 'status'
